import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlarmClock } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { timeTrackingApi } from '@/features/time-tracking/api';
import { formatDuration } from '@/utils/format';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'] as const;
const CHECK_INTERVAL_MS = 15_000;

/**
 * Idle Detection — mounted once in AppLayout, watches for user activity
 * app-wide while a timer is running. When the org's idle threshold
 * (time_tracking_settings.idle_threshold_minutes) is crossed, prompts
 * the user to keep or discard the idle stretch rather than silently
 * padding their tracked hours. Polling-based (no Page Visibility/Idle
 * API dependency), consistent with this platform's lightweight,
 * shared-hosting-friendly client patterns.
 */
export function IdleDetectionMonitor() {
  const queryClient = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ['time-tracking', 'settings'], queryFn: timeTrackingApi.getSettings, staleTime: 5 * 60_000 });
  const { data: current } = useQuery({ queryKey: ['time-tracking', 'current'], queryFn: timeTrackingApi.current, refetchInterval: 20_000 });

  const lastActivityRef = useRef(Date.now());
  const [idleSecondsDetected, setIdleSecondsDetected] = useState<number | null>(null);

  useEffect(() => {
    function markActive() {
      lastActivityRef.current = Date.now();
    }
    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, markActive, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, markActive));
  }, []);

  useEffect(() => {
    if (!current || idleSecondsDetected !== null) return;
    const thresholdMs = (settings?.idle_threshold_minutes ?? 5) * 60_000;

    const interval = setInterval(() => {
      const idleMs = Date.now() - lastActivityRef.current;
      if (idleMs >= thresholdMs) {
        setIdleSecondsDetected(Math.round(idleMs / 1000));
      }
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [current, settings, idleSecondsDetected]);

  const discardMutation = useMutation({
    mutationFn: (seconds: number) => timeTrackingApi.reportIdle(seconds),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['time-tracking'] });
      resolvePrompt();
    },
  });

  function resolvePrompt() {
    lastActivityRef.current = Date.now();
    setIdleSecondsDetected(null);
  }

  if (!current || idleSecondsDetected === null) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-sm text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-400">
          <AlarmClock className="h-6 w-6" />
        </div>
        <h3 className="mb-1 text-base font-semibold text-gray-900 dark:text-gray-100">Still tracking "{current.task_title}"?</h3>
        <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
          You've been idle for {formatDuration(Math.round(idleSecondsDetected / 60))}. Keep it as tracked time, or discard it from this entry?
        </p>
        <div className="flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={resolvePrompt}>Keep time</Button>
          <Button className="flex-1" isLoading={discardMutation.isPending} onClick={() => discardMutation.mutate(idleSecondsDetected)}>
            Discard idle time
          </Button>
        </div>
      </Card>
    </div>
  );
}
